import API from './api';

const adminService = {
  // Auth token management
  setAuthToken: (token) => {
    if (token) {
      localStorage.setItem('adminToken', token);
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem('adminToken');
      delete API.defaults.headers.common['Authorization'];
    }
  },

  // Auth
  login: async (credentials) => {
    try {
      const response = await API.post('/admin/auth/login', credentials);
      if (response.data.success && response.data.token) {
        adminService.setAuthToken(response.data.token);
      }
      return response;
    } catch (error) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message || 'Login failed');
      }
      throw error;
    }
  },
  
  logout: () => {
    adminService.setAuthToken(null);
    return API.post('/admin/auth/logout');
  },
  
  refreshToken: () => API.post('/admin/auth/refresh-token'),
  getCurrentAdmin: () => API.get('/admin/auth/me'),
  
  // Initialize auth from stored token
  initializeAuth: () => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      API.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  },

  // Dashboard
  getDashboardStats: () => API.get('/admin/dashboard/stats'),
  getRecentActivity: () => API.get('/admin/dashboard/recent-activity'),
  getRevenueSummary: (params) => API.get('/admin/dashboard/revenue-summary', { params }),

  // Users
  getUsers: (params) => API.get('/admin/users', { params }),
  getUserCategoryStats: (params) => API.get('/admin/users/stats/categories', { params }),
  getRecentUserStats: () => API.get('/admin/users/stats/recent'),
  getPaymentAttempts: (params) => API.get('/admin/payment-attempts', { params }),
  createUser: (data) => API.post('/admin/users', data),
  getUserDetails: (userId) => API.get(`/admin/users/${userId}`),
  updateUserStatus: (userId, data) => API.put(`/admin/users/${userId}/status`, data),
  updateUser: (userId, data) => API.put(`/admin/users/${userId}`, data),
  uploadUserPhoto: (userId, formData) => API.post(`/admin/users/${userId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteUserPhoto: (userId, photoId) => API.delete(`/admin/users/${userId}/photos/${photoId}`),
  setUserProfilePic: (userId, photoId) => API.put(`/admin/users/${userId}/photos/${photoId}/set-profile-pic`),
  deleteUser: (userId) => API.delete(`/admin/users/${userId}`),

  // Documents
  getDocuments: (params) => API.get('/admin/documents', { params }),
  verifyDocument: (documentId, data) => API.patch(`/admin/documents/${documentId}/verify`, data),

  // Profiles
  getProfiles: (params) => API.get('/admin/profiles', { params }),
  updateProfile: (profileId, data) => API.patch(`/admin/profiles/${profileId}`, data),

  // About Me moderation
  getAboutMeReviewQueue: (params) => API.get('/admin/about-me/review-queue', { params }),
  reviewAboutMe: (profileId, data) => API.put('/admin/about-me/' + profileId + '/review', data),
  getAboutMeVerificationHistory: (profileId) => API.get('/admin/about-me/' + profileId + '/history'),

  // Photo moderation
  getPhotoReviewQueue: (params) => API.get('/admin/photos/review-queue', { params }),
  reviewPhoto: (profileId, photoId, data) => API.put(`/admin/photos/${profileId}/${photoId}/review`, data),
  getPhotoVerificationHistory: (profileId) => API.get(`/admin/photos/${profileId}/history`),

  // Subscriptions (formerly Memberships)
  getSubscriptions: (params) => API.get('/subscriptions/users', { params }),
  assignPlan: (data) => API.post('/subscriptions/admin/assign', data),
  cancelSubscription: (userId, data) => API.put(`/subscriptions/cancel/${userId}`, data),
  extendSubscription: (userId, data) => API.put(`/subscriptions/extend/${userId}`, data),
  getSubscriptionAnalytics: () => API.get('/subscriptions/analytics'),
  bulkUpdateSubscriptions: (data) => API.put('/subscriptions/bulk-update', data),

  // Legacy Memberships (deprecated - use subscriptions instead)
  getMemberships: (params) => API.get('/subscriptions/users', { params }),
  updateMembership: (userId, data) => API.post('/subscriptions/admin/assign', { userId, ...data }),

  // Plans Management
  getPlans: (params) => API.get('/plans', { params }),
  createPlan: (data) => API.post('/plans', data),
  updatePlan: (planId, data) => API.put(`/plans/${planId}`, data),
  deletePlan: (planId) => API.delete(`/plans/${planId}`),
  getPlanAnalytics: () => API.get('/plans/analytics'),

  // Settings
  getSettings: () => API.get('/admin/settings'),
  updateSettings: (data) => API.post('/admin/settings', data),

  // Admin account management (superadmin only)
  getAdminAccounts: () => API.get('/admin/admins'),
  createAdminAccount: (data) => API.post('/admin/admins', data),
  updateAdminAccount: (adminId, data) => API.put(`/admin/admins/${adminId}`, data),

  // Activity Logs
  getActivityLogs: (params) => API.get('/admin/logs', { params }),
};

// Initialize authentication on service import
adminService.initializeAuth();

export default adminService;
